import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

export const NONE = "__none__";
export type LateralFilterContext = "list" | "kanban";

export type SavedLateralFilterSet = {
  id: string;
  name: string;
  filters: Record<string, any>;
};

function lsKey(context: LateralFilterContext) {
  return `lateral_filter_sets_${context}`;
}

function readFromStorage(context: LateralFilterContext): SavedLateralFilterSet[] {
  try {
    const raw = localStorage.getItem(lsKey(context));
    if (raw) return JSON.parse(raw) as SavedLateralFilterSet[];
  } catch {}
  return [];
}

function writeToStorage(context: LateralFilterContext, sets: SavedLateralFilterSet[]) {
  localStorage.setItem(lsKey(context), JSON.stringify(sets));
}

export function useLateralSavedFilters(context: LateralFilterContext) {
  const { toast } = useToast();
  const [savedFilters, setSavedFilters] = useState<SavedLateralFilterSet[]>(
    () => readFromStorage(context)
  );
  const [selected, setSelected] = useState<string>(NONE);

  const saveCurrentFilters = (currentFilters: Record<string, any>) => {
    const name = window.prompt("Name this filter set")?.trim();
    if (!name) return;

    const existing = readFromStorage(context);
    const id = existing.find((f) => f.name === name)?.id ?? crypto.randomUUID();
    const updated = [
      ...existing.filter((f) => f.name !== name),
      { id, name, filters: currentFilters },
    ].sort((a, b) => a.name.localeCompare(b.name));

    writeToStorage(context, updated);
    setSavedFilters(updated);
    setSelected(name);
    toast({ title: "Filter saved", description: `"${name}" saved.` });
  };

  const deleteSelected = () => {
    if (selected === NONE) return;
    if (!window.confirm(`Delete filter "${selected}"?`)) return;

    const updated = readFromStorage(context).filter((f) => f.name !== selected);
    writeToStorage(context, updated);
    setSavedFilters(updated);
    setSelected(NONE);
    toast({ title: "Filter deleted", description: `"${selected}" removed.` });
  };

  return {
    savedFilters,
    selected,
    setSelected,
    saveCurrentFilters,
    deleteSelected,
    isSaving: false,
    isDeleting: false,
  };
}
